import { List, Map as ImmutableMap } from 'immutable';
import memoize from 'transmute/memoize';
import { DEAL_GROUPING_TYPE, DEAL_STAGE, FORECAST_CATEGORY } from '../constants/DealGroupingType';
// Type guard to check if a dealGrouping is a DealStage
export const isDealStage = dealGrouping => {
  return (dealGrouping === null || dealGrouping === void 0 ? void 0 : dealGrouping.stageId) !== undefined;
};
export const getDealGrouping = ({
  groupingId,
  dealGroupings
}) => {
  return dealGroupings === null || dealGroupings === void 0 ? void 0 : dealGroupings.find(grouping => isDealStage(grouping) ? grouping.stageId === groupingId : grouping.value === groupingId);
};

// derives the default list of deal groupings values/ids (removes omitted and closed groupings)
export const deriveDefaultDealGroupingsOld = (dealGroupingPropertyValues, omittedGroupIds = List(), closedGroupIds = List()) => dealGroupingPropertyValues.filter(grouping => {
  if (isDealStage(grouping)) {
    return !omittedGroupIds.includes(grouping.stageId) && !closedGroupIds.includes(grouping.stageId);
  }
  return !omittedGroupIds.includes(grouping.value) && !closedGroupIds.includes(grouping.value);
}).reduce((filterList, grouping) => {
  if (isDealStage(grouping)) {
    filterList.push(grouping.stageId);
  } else {
    filterList.push(grouping.value);
  }
  return filterList;
}, []);

// derives the default list of deal groupings values/ids (removes omitted groupings)
export const deriveDefaultDealGroupings = (dealGroupingPropertyValues, omittedGroupIds = List()) => dealGroupingPropertyValues.filter(grouping => {
  if (isDealStage(grouping)) {
    return !omittedGroupIds.includes(grouping.stageId);
  }
  return !omittedGroupIds.includes(grouping.value);
}).reduce((filterList, grouping) => {
  if (isDealStage(grouping)) {
    filterList.push(grouping.stageId);
  } else {
    filterList.push(grouping.value);
  }
  return filterList;
}, []);

// sorts deal groupings by display order
export const deriveSortedDealGroupingOptions = dealGroupingPropertyValues => {
  var _ref2;
  if (!dealGroupingPropertyValues) {
    return [];
  }
  if (isDealStage(dealGroupingPropertyValues[0])) {
    var _ref;
    return (_ref = [...dealGroupingPropertyValues]) === null || _ref === void 0 ? void 0 : _ref.sort((a, b) => {
      return a.displayOrder - b.displayOrder;
    });
  }
  return (_ref2 = [...dealGroupingPropertyValues]) === null || _ref2 === void 0 ? void 0 : _ref2.sort((a, b) => {
    return a.displayOrder - b.displayOrder;
  });
};

// sorts deal groupings, removes the closed and omitted ones, and then finally slices the list so it
//  is not larger than the column limit for that grouping
export const deriveFilteredDealGroupings = (dealGroupingPropertyValues, dealGroupingType, omittedGroupIds = List(), closedGroupIds = List()) => {
  const sortedGroupings = deriveSortedDealGroupingOptions(dealGroupingPropertyValues);
  let result;
  if (dealGroupingType === FORECAST_CATEGORY) {
    result = sortedGroupings.filter(grouping => !omittedGroupIds.includes(grouping.value) && !closedGroupIds.includes(grouping.value)).slice(DEAL_GROUPING_TYPE[dealGroupingType].columnLimit);
  } else {
    result = sortedGroupings.filter(grouping => !omittedGroupIds.includes(grouping.stageId) && !closedGroupIds.includes(grouping.stageId)).slice(DEAL_GROUPING_TYPE[dealGroupingType].columnLimit);
  }
  return result;
};
export const getDealStageIds = dealStages => {
  return dealStages.map(dealStage => dealStage.stageId);
};
export const getForecastCategoryIds = property => {
  var _property$options;
  return property === null || property === void 0 || (_property$options = property.options) === null || _property$options === void 0 ? void 0 : _property$options.map(option => option.value);
};
export const filterValidSelectedDealGroupingIds = ({
  dealGroupingIds,
  selectedDealGroupings
}) => {
  return dealGroupingIds.filter(grouping => selectedDealGroupings === null || selectedDealGroupings === void 0 ? void 0 : selectedDealGroupings.includes(grouping));
};
export const makeDealGroupingSelectOptions = memoize(({
  dealGroupingType,
  dealStages,
  forecastCategoryProperty
}) => {
  var _deriveSortedDealGrou;
  const dealGroupings = dealGroupingType === DEAL_STAGE ? dealStages : forecastCategoryProperty === null || forecastCategoryProperty === void 0 ? void 0 : forecastCategoryProperty.options;
  return (_deriveSortedDealGrou = deriveSortedDealGroupingOptions(dealGroupings)) === null || _deriveSortedDealGrou === void 0 ? void 0 : _deriveSortedDealGrou.map(grouping => {
    if (isDealStage(grouping)) {
      return {
        text: grouping.label,
        value: grouping.stageId
      };
    } else {
      return {
        text: grouping.label,
        value: grouping.value
      };
    }
  });
});

//for the edge case in which the deal stage -> forecast category mapping did not work
//in this case, the forecast category property will not exist on a deal
//so we manually add an empty category value so the page does not crash
//does not happen for deal stage but keeping things generic and using deal grouping
export const cleanUpDealGroupingsWhenNonExistent = (deals, dealGroupingPropertyPathToUse) => deals.map(deal => {
  deal.properties[dealGroupingPropertyPathToUse] = deal.properties[dealGroupingPropertyPathToUse] ? deal.properties[dealGroupingPropertyPathToUse] : {
    value: ''
  };
  return deal;
});
const computeDealStageGroupingDisplayOptions = sortOptionsToUse => {
  return sortOptionsToUse.reduce((acc, dealGrouping) => {
    return acc.set(dealGrouping.stageId, dealGrouping.displayOrder);
  }, ImmutableMap());
};
const computeForecastCategoryGroupingDisplayOptions = sortOptionsToUse => {
  return sortOptionsToUse.reduce((acc, dealGrouping) => {
    return acc.set(dealGrouping.value, dealGrouping.displayOrder);
  }, ImmutableMap());
};
export const sortDealsByDealGroupingOrder = (deals, sortOptionsToUse, dealGroupingType) => {
  const groupingDisplayOptions = isDealStage(sortOptionsToUse[0]) ? computeDealStageGroupingDisplayOptions(sortOptionsToUse) : computeForecastCategoryGroupingDisplayOptions(sortOptionsToUse);
  const propertyPathToUse = DEAL_GROUPING_TYPE[dealGroupingType].propertyName;
  const dealsWithGroupings = cleanUpDealGroupingsWhenNonExistent(deals, propertyPathToUse);
  return dealsWithGroupings.sort((a, b) => {
    var _a$properties$propert, _b$properties$propert;
    return groupingDisplayOptions.get(a === null || a === void 0 || (_a$properties$propert = a.properties[propertyPathToUse]) === null || _a$properties$propert === void 0 ? void 0 : _a$properties$propert.value) - groupingDisplayOptions.get(b === null || b === void 0 || (_b$properties$propert = b.properties[propertyPathToUse]) === null || _b$properties$propert === void 0 ? void 0 : _b$properties$propert.value);
  });
};